import React, { useState } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Import sample data
import { shopCategoriesL1, shopLocationTypes, productDistributionByShopType } from '../../data/shopData';
import { productCategoriesL1 } from '../../data/productData';

const ShopCategoryOverview = () => {
  const [selectedMetric, setSelectedMetric] = useState('totalSales');
  const [selectedShopCategory, setSelectedShopCategory] = useState('all');
  
  // Format metric values for display
  const formatMetricValue = (value) => {
    switch(selectedMetric) {
      case 'totalSales':
        return `$${(value/1000000).toFixed(2)}M`;
      case 'growth':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString();
    }
  };
  
  // Prepare data for pie chart
  const preparePieData = () => {
    return shopCategoriesL1.map(category => ({
      name: category.name,
      value: category[selectedMetric]
    }));
  };
  
  // Prepare data for shop location types chart
  const prepareLocationTypeData = () => {
    return shopLocationTypes.map(type => ({
      name: type.name,
      value: type[selectedMetric === 'totalSales' ? 'totalSales' : 
                  selectedMetric === 'growth' ? 'growth' : 'count']
    }));
  };
  
  // Prepare product distribution by shop type
  const prepareProductDistribution = () => {
    const shopCatId = selectedShopCategory === 'all' ? 1 : parseInt(selectedShopCategory);
    
    return productDistributionByShopType
      .filter(d => d.shopCategoryL1Id === shopCatId)
      .map(d => {
        const productCategory = productCategoriesL1.find(pc => pc.id === d.productCategoryL1Id);
        return {
          name: productCategory ? productCategory.name : `Category ${d.productCategoryL1Id}`,
          percentage: d.salesPercentage,
          averagePrice: d.averagePrice
        };
      })
      .sort((a, b) => b.percentage - a.percentage);
  };
  
  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1', '#A4DE6C', '#D0ED57'];
  
  // Calculate summary statistics
  const totalSales = shopCategoriesL1.reduce((sum, category) => sum + category.totalSales, 0);
  const avgGrowth = shopCategoriesL1.reduce((sum, category) => sum + category.growth, 0) / shopCategoriesL1.length;
  const totalShops = shopCategoriesL1.reduce((sum, category) => sum + category.shopCount, 0);
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Shop Category Overview</h1>
        <p className="text-gray-600 mb-4">
          Analyze different types of retail channels, their performance metrics, and product mix characteristics.
        </p>
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="kpi-card">
            <h3 className="kpi-label">Total Sales</h3>
            <p className="kpi-value">${(totalSales / 1000000).toFixed(1)}M</p>
            <p className="text-sm text-gray-600">Across {shopCategoriesL1.length} shop categories</p>
          </div>
          
          <div className="kpi-card">
            <h3 className="kpi-label">Total Shops</h3>
            <p className="kpi-value">{totalShops.toLocaleString()}</p>
            <p className="text-sm text-gray-600">All locations</p>
          </div>
          
          <div className="kpi-card">
            <h3 className="kpi-label">Average Growth</h3>
            <p className="kpi-value">{avgGrowth.toFixed(1)}%</p>
            <p className="text-sm text-gray-600">Year-over-year</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Performance Metric</label>
            <select 
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="dropdown-selector"
            >
              <option value="totalSales">Total Sales (USD)</option>
              <option value="growth">Growth Rate (%)</option>
              <option value="shopCount">Shop Count</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-3">Shop Category for Product Mix</label>
            <select 
              value={selectedShopCategory}
              onChange={(e) => setSelectedShopCategory(e.target.value)}
              className="dropdown-selector"
            >
              <option value="all">All Shop Categories</option>
              {shopCategoriesL1.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Shop Category Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="chart-container">
          <h2 className="chart-title">Shop Category Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={preparePieData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {preparePieData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatMetricValue(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="chart-container">
          <h2 className="chart-title">Shop Location Type Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={prepareLocationTypeData()}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => formatMetricValue(value)} />
                <Legend />
                <Bar 
                  dataKey="value" 
                  fill="#00C49F" 
                  name={selectedMetric === 'totalSales' 
                        ? 'Total Sales' 
                        : selectedMetric === 'growth'
                        ? 'Growth Rate'
                        : 'Shop Count'} 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Product Category Mix */}
      <div className="chart-container mb-6">
        <h2 className="chart-title">
          Product Category Mix
          {selectedShopCategory !== 'all' && 
            ` for ${shopCategoriesL1.find(c => c.id === parseInt(selectedShopCategory))?.name || 'Selected Category'}`
          }
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={prepareProductDistribution()}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 90, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit="%" />
              <YAxis type="category" dataKey="name" />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="percentage" fill="#FF8042" name="Sales Percentage" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Shop Category Performance Table */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Shop Category Performance</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Total Sales</th>
                <th>Growth</th>
                <th>Shop Count</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {[...shopCategoriesL1]
                .sort((a, b) => b[selectedMetric] - a[selectedMetric])
                .map(category => (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>${(category.totalSales / 1000000).toFixed(2)}M</td>
                    <td className={category.growth >= 0 ? 'positive-growth' : 'negative-growth'}>
                      {category.growth >= 0 ? '↑' : '↓'} {Math.abs(category.growth).toFixed(1)}%
                    </td>
                    <td>{category.shopCount.toLocaleString()}</td>
                    <td>{((category.totalSales / totalSales) * 100).toFixed(1)}%</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Product Mix Detail */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Product Mix Details
          {selectedShopCategory !== 'all' && 
            ` for ${shopCategoriesL1.find(c => c.id === parseInt(selectedShopCategory))?.name || 'Selected Category'}`
          }
        </h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Product Category</th>
                <th>Sales Percentage</th>
                <th>Average Price</th>
              </tr>
            </thead>
            <tbody>
              {prepareProductDistribution().map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{item.percentage}%</td>
                  <td>${item.averagePrice}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ShopCategoryOverview;